/**
 * The rules behind RichTextEditor that do not need a DOM, so they can be tested without one — this
 * project's Vitest has no jsdom, and an editor's rules are exactly the kind of thing that breaks
 * quietly.
 *
 * Every rule here is the client half of a rule the server already enforces. The body is sanitised
 * where it is stored (akis-backend HtmlSanitizer), so nothing below is a security boundary; what it
 * buys is that the editor never shows the author something the server is going to throw away. A
 * link the sanitiser drops is a link that vanishes between "Salvează" and the public page, with no
 * message anywhere — refusing it here, in words, is the difference.
 */
import type { TextLimitBand } from '@/features/common/types/TextLimitBand';
import { mediaApi } from '@/services/mediaApi';

/**
 * The address a link will actually point at, or null when the server would drop it.
 *
 * HtmlSanitizer keeps an `href` only if the WHOLE value is an http(s) URL, a mailto, a site-rooted
 * path or a fragment — never a protocol-relative `//host`, which names no protocol and so slips
 * past a protocol filter while leaving the site. Those four shapes pass through unchanged.
 *
 * Two shapes people actually type are completed rather than refused, because both have one obvious
 * meaning: a bare address (`www.gov.md`, `gov.md/stiri`) becomes https, and a bare e-mail becomes a
 * mailto. Anything else — `javascript:`, `ftp:`, text with spaces — is refused.
 */
export function normalizeLinkHref(input: string): string | null {
  const value = input.trim();
  if (!value || /\s/.test(value)) return null;
  if (/^https?:\/\/\S+$/i.test(value)) return value;
  if (/^mailto:\S+$/i.test(value)) return value;
  if (value.startsWith('#')) return value;
  if (value.startsWith('/')) return value.startsWith('//') ? null : value;
  if (/^[^@/:]+@[^@/:]+\.[^@/:]+$/.test(value)) return `mailto:${value}`;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?(\/\S*)?$/i.test(value)) return `https://${value}`;
  return null;
}

// The entities a body's text can hold: the browser writes &amp; &lt; &gt; and &nbsp; when it
// serialises an editor, and the server's sanitiser writes characters such as " and @ as numbers.
const ENTITY = /&(?:#(\d+)|#x([0-9a-f]+)|(amp|lt|gt|quot|apos|nbsp));/gi;
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

/**
 * The text of a body as a reader gets it: the tags gone, each entity back to the one character it
 * stands for.
 *
 * The one extraction behind both "is it blank?" and "how long is it?", so the two cannot disagree
 * about what counts as text. Decoded in a single pass, so `&amp;lt;` stays the four characters
 * `&lt;` that someone typed.
 */
export function htmlText(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(ENTITY, (entity, decimal, hex, name) => {
    if (name) return NAMED_ENTITIES[name.toLowerCase()];
    const code = decimal ? Number(decimal) : parseInt(hex, 16);
    return code <= 0x10ffff ? String.fromCodePoint(code) : entity;
  });
}

/**
 * Whether a body holds nothing a reader would see.
 *
 * A contentEditable that has been typed into and emptied again is not empty markup: the browser
 * leaves `<p><br></p>` or `<br>` behind, and a check on `.trim()` reads that as content — so an
 * article could be submitted for approval with an invisible body. An image is content even though
 * it has no text. A no-break space is not: trim() treats it as whitespace once htmlText has turned
 * the entity back into the character.
 */
export function isBlankHtml(html: string | null | undefined): boolean {
  if (!html) return true;
  if (/<img\b/i.test(html)) return false;
  return htmlText(html).trim() === '';
}

/**
 * How many characters of text a body holds: markup not counted, an entity counted once. Counted in
 * code points, so an emoji is one character, as the author sees it, not the two a string holds.
 */
export function htmlTextLength(html: string | null | undefined): number {
  return Array.from(htmlText(html)).length;
}

/** Where a length stands against its limit: `near` from 90% of it, `over` only past it. */
export function textLimitBand(length: number, limit: number): TextLimitBand {
  if (length > limit) return 'over';
  return length >= limit * 0.9 ? 'near' : 'within';
}

/**
 * 20 000 / 20,000 — digits grouped the way the page's language groups them. The Romanian separator
 * is a no-break space, so a count never breaks across two lines.
 */
export function formatCount(n: number, language: string): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, language === 'en' ? ',' : ' ');
}

/** Text as it has to be written inside markup, attribute values included. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * An image as the body stores it. `alt` is required by the caller, not defaulted here: a file name
 * is not a description, and an image a screen reader announces as "IMG_2034" has failed WCAG 1.1.1
 * as surely as one with no alt at all.
 */
export function imageHtml(assetId: string, alt: string): string {
  return `<img src="${mediaApi.downloadUrl(assetId)}" alt="${escapeHtml(alt.trim())}">`;
}

/**
 * An image put into the text before anyone has described it — the one way in that imageHtml's
 * contract does not allow, kept as a separate function so that contract stays true for every other
 * caller.
 *
 * The editor inserts the picture the moment it is uploaded and asks for the description beside it
 * afterwards, rather than blocking the author on a form before they have seen what went in. The
 * empty `alt` is the open task, not a claim that the image is decorative: imagesMissingAlt counts it,
 * and a submission for approval is refused while any is left.
 */
export function imageHtmlPending(assetId: string): string {
  return `<img src="${mediaApi.downloadUrl(assetId)}" alt="">`;
}

const IMAGE_TAG = /<img\b[^>]*>/gi;
const ALT_ATTRIBUTE = /\salt\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i;

/**
 * How many images a body holds that a screen reader could not describe: `alt` absent, empty or only
 * spaces. Read from the stored markup, not from a live editor, so the gate before submission holds
 * whatever the editor did and is testable without a DOM.
 */
export function imagesMissingAlt(html: string | null | undefined): number {
  if (!html) return 0;
  return (html.match(IMAGE_TAG) ?? []).filter((tag) => {
    const alt = ALT_ATTRIBUTE.exec(tag);
    return !alt || (alt[1] ?? alt[2] ?? alt[3] ?? '').trim() === '';
  }).length;
}

/**
 * The images without a description across a form's bodies: the Romanian one always, the English
 * one only while the form is bilingual — an unticked English body is not sent, so nothing in it
 * reaches the public page. News and pages both call it before a submission for approval.
 */
export function formImagesMissingAlt(form: {
  bodyRo: string;
  bilingual: boolean;
  bodyEn: string;
}): number {
  return imagesMissingAlt(form.bodyRo) + (form.bilingual ? imagesMissingAlt(form.bodyEn) : 0);
}

/** 1,2 MB / 1.2 MB — the decimal separator of the language the page is in. */
export function formatFileSize(bytes: number, language: string): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  const mb = (bytes / (1024 * 1024)).toFixed(1);
  return `${language === 'en' ? mb : mb.replace('.', ',')} MB`;
}

/**
 * An attached file as a link, with its type and size after it: "Raport anual.pdf (PDF, 1,2 MB)".
 *
 * The name is the link text because it is what the reader will get, and the type and size follow
 * because a download is a commitment a plain link is not — WCAG 2.4.4 asks that the purpose of a
 * link be clear from its text, and "a 40 MB spreadsheet" is part of the purpose.
 */
export function attachmentHtml(
  asset: { id: string; name: string; sizeBytes: number },
  language: string,
): string {
  const dot = asset.name.lastIndexOf('.');
  const extension = dot > 0 ? asset.name.slice(dot + 1).toUpperCase() : '';
  const size = formatFileSize(asset.sizeBytes, language);
  // The extension is part of the file name, and as untrusted as the rest of it: "x.<b>" is a name.
  return `<a href="${mediaApi.downloadUrl(asset.id)}">${escapeHtml(asset.name)}</a> (${
    extension ? `${escapeHtml(extension)}, ` : ''
  }${size})`;
}
